import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Textarea } from "@material-tailwind/react";
import useAuth from "../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { PROXY } from "../lib/utils";
import { toast } from "react-toastify";
import ReviewRatingInput from "./ReviewRatingInput";
import { isDuplicateReviewContent } from "../lib/reviewUtils";
import { clearPendingCourseAction, readPendingCourseAction } from "../lib/pendingCourseAction";

const ReviewForm = ({ mutate, courseId, reviews = [], onLoginRequired }) => {
  const { register, handleSubmit, reset, getValues, formState: { errors }, setError } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [rating, setRating] = useState(1);
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const restoredRef = useRef(false);

  const { user, accessToken, isAuth } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuth || !courseId || restoredRef.current) return;
    const pending = readPendingCourseAction();
    if (pending?.action !== "review" || pending.courseId !== String(courseId)) return;
    restoredRef.current = true;
    reset({ content: pending.draft.content });
    setRating(pending.draft.rating);
    toast.info(t("courseGuest.draftRestored"));
  }, [isAuth, courseId, reset, t]);

  useEffect(() => {
    if (window.location.hash !== "#course-review-form") return undefined;
    const frame = requestAnimationFrame(() => document.getElementById("course-review-form")?.scrollIntoView({ block: "start" }));
    return () => cancelAnimationFrame(frame);
  }, [courseId, isAuth]);

  const onSubmit = async (data) => {
    if (!isAuth) {
      onLoginRequired?.("review", { content: data.content, rating });
      return;
    }
    if (rating === 0) {
      setError("rating", { message: t("ReviewForm.SelectRating") });
      return;
    }

    if (isDuplicateReviewContent({ reviews, userId: user?.id, content: data.content })) {
      const duplicateMessage = t("validation.review_duplicate");
      setError("content", { message: duplicateMessage });
      toast.warn(duplicateMessage, { position: "top-center" });
      return;
    }

    try {
      if (courseId) {
        setIsLoading(true);

        const response = await fetch(`${PROXY}/courses/reviews/${courseId}`, {
          method: "PUT",
          headers: {
            "Authorization": `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            rating,
            startedAt,
          }),
        });

        const result = await response.json();

        if (response.ok && response.status !== 299) {
          toast.success(t("toast.review-success"), { position: "top-center" });
          mutate();
          setRating(1);
          reset();
          setStartedAt(Date.now());
          const pending = readPendingCourseAction();
          if (pending?.action === "review" && pending.courseId === String(courseId)) {
            clearPendingCourseAction();
          }
          return;
        }

        if (response.status === 409 || result?.code === "DUPLICATE_REVIEW") {
          const duplicateMessage = t("validation.review_duplicate");
          setError("content", { message: duplicateMessage });
          toast.warn(result?.message || t("toast.review-duplicate"), { position: "top-center" });
          return;
        }

        toast.error(result?.message || t("toast.review-Error"), { position: "top-center" });
      }
    } catch (error) {
      toast.error(t("toast.review-Error"), { position: "top-center" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form id="course-review-form" onSubmit={handleSubmit(onSubmit)} className="w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-5 shadow-sm my-16">
      <input
        type="text"
        tabIndex="-1"
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
        {...register("website")}
      />

      <ReviewRatingInput
        value={rating}
        onChange={(value) => {
          setRating(value);
          if (!isAuth) onLoginRequired?.("review", { content: getValues("content"), rating: value });
        }}
        label={t("ReviewForm.SelectRating")}
        disabled={isLoading}
      />

      {errors.rating && <p className="text-red-500 text-xs mt-1">{errors.rating.message}</p>}

      <div className="mb-4 mt-6 w-full">
        <Textarea
          label={t("ReviewForm.WriteReview")}
          error={!!errors.content}
          {...register("content", { required: t("ReviewForm.ReviewRequired") })}
          onFocus={() => {
            if (!isAuth) onLoginRequired?.("review", { content: getValues("content"), rating });
          }}
          className="pb-2 min-h-28"
          color="indigo"
        />
        {errors.content && <p className="text-red-500 text-xs mt-1">{errors.content.message}</p>}
      </div>

      <Button
        type="submit"
        color="indigo"
        className="mt-2 min-w-40"
        disabled={isLoading}
      >
        {isLoading ? t("ReviewForm.Submitting") : t("ReviewForm.SubmitReview")}
      </Button>
    </form>
  );
};


export default ReviewForm;
